'use client';

import { ALL_STATES } from '@/constants/location_states';
import { toCleanSlug, toInternalDistrictSlug, toInternalStateSlug } from '@/lib/location_slug_mapper';
import { useLazyAllDistricts } from '@/hooks/useLazyAllDistricts';
import { useRouter } from 'next/navigation';
import { useEffect, useMemo, useState } from 'react';

type UpdateType = 'Daily' | 'Monthly' | 'Yearly';
type MetalType = 'gold' | 'silver';
const MONTH_OPTIONS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

interface GoldSilverFiltersProps {
  initialStateSlug: string;
  initialCitySlug?: string;
  initialMetal: MetalType;
  initialDate: string;
  initialType?: UpdateType;
  initialMonth?: string;
  initialYear?: string;
}

export default function GoldSilverFilters({
  initialStateSlug,
  initialCitySlug,
  initialMetal,
  initialDate,
  initialType = 'Daily',
  initialMonth = '',
  initialYear = '',
}: GoldSilverFiltersProps) {
  const router = useRouter();
  const initialStateInternal = toInternalStateSlug(initialStateSlug) || '';
  const initialCityInternal =
    initialStateInternal && initialCitySlug
      ? toInternalDistrictSlug({
          stateInternalSlug: initialStateInternal,
          districtParam: initialCitySlug,
        }) || ''
      : '';
  const normalizeInitialMonth = (monthValue: string) => {
    const trimmed = monthValue.trim();
    if (!trimmed) {
      return '';
    }
    const [monthToken] = trimmed.split('-');
    const normalizedToken = monthToken.toLowerCase();
    const matchedMonth = MONTH_OPTIONS.find(
      (month) =>
        month.toLowerCase() === normalizedToken ||
        month.toLowerCase().startsWith(normalizedToken),
    );
    return matchedMonth || '';
  };

  const [stateSlug, setStateSlug] = useState(initialStateInternal);
  const [citySlug, setCitySlug] = useState(initialCityInternal);
  const [updateType, setUpdateType] = useState<UpdateType>(initialType);
  const [selectedMonth, setSelectedMonth] = useState(normalizeInitialMonth(initialMonth));
  const [selectedYear, setSelectedYear] = useState(initialYear);
  const [isLoading, setIsLoading] = useState(false);
  const allDistricts = useLazyAllDistricts();

  const stateOptions = useMemo(
    () =>
      Object.entries(ALL_STATES).map(([slug, state]) => ({
        slug,
        label: state.inline_label || state.label,
        code: state.value,
      })),
    [],
  );

  const selectedStateCode =
    stateOptions.find((state) => state.slug === stateSlug)?.code || '';

  const cityOptions = useMemo(() => {
    if (!selectedStateCode || !allDistricts) {
      return [];
    }
    return Object.entries(allDistricts[selectedStateCode] || {}).map(([slug, city]) => ({
      slug,
      label: city.label,
      code: city.value,
    }));
  }, [selectedStateCode, allDistricts]);

  const selectedCityCode = cityOptions.find((city) => city.slug === citySlug)?.code || '';
  const metalSlug = initialMetal === 'gold' ? 'gold-price' : 'silver-price';
  const yearOptions = useMemo(() => {
    const currentYear = new Date().getFullYear();
    return Array.from(
      { length: currentYear - 2020 + 1 },
      (_, index) => String(2020 + index),
    );
  }, []);

  const toMonthlySlug = (monthName: string) => {
    if (!monthName) {
      return '';
    }
    const now = new Date();
    const currentMonthIndex = now.getMonth() + 1;
    const currentYear = now.getFullYear();
    const selectedMonthIndex = new Date(`${monthName} 1, 2000`).getMonth() + 1;
    const inferredYear =
      selectedMonthIndex > currentMonthIndex ? currentYear - 1 : currentYear;
    return `${monthName.slice(0, 3).toLowerCase()}-${inferredYear}`;
  };

  useEffect(() => {
    if (!selectedStateCode) {
      return;
    }
    let cancelled = false;
    const fetchData = async () => {
      try {
        setIsLoading(true);
        const params = new URLSearchParams({
          type: updateType,
          stateCode: selectedStateCode,
          page: '1',
          pageSize: '100',
        });
        if (selectedCityCode) {
          params.set('cityCode', selectedCityCode);
        }
        if (updateType === 'Monthly' && selectedMonth) {
          params.set('month', selectedMonth);
        }
        if (updateType === 'Yearly' && selectedYear) {
          params.set('year', selectedYear);
        }
        await fetch(`/api/public-updates/gold-silver?${params.toString()}`, {
          cache: 'no-store',
        });
      } catch (error) {
        console.error('gold-silver-filters:fetch-error', error);
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    };
    fetchData();
    return () => {
      cancelled = true;
    };
  }, [selectedStateCode, selectedCityCode, updateType, selectedMonth, selectedYear]);

  const onApply = () => {
    if (!stateSlug) {
      return;
    }
    const cleanState = toCleanSlug(stateSlug);
    if (!citySlug) {
      const params = new URLSearchParams();
      if (updateType === 'Yearly') {
        params.set('type', 'Yearly');
        if (selectedYear) {
          params.set('year', selectedYear);
        }
      } else if (updateType === 'Monthly') {
        params.set('type', 'Monthly');
        if (selectedMonth) {
          params.set('month', selectedMonth);
        }
      }
      const query = params.toString();
      router.push(
        query
          ? `/gold-silver-prices/${metalSlug}/${cleanState}?${query}`
          : `/gold-silver-prices/${metalSlug}/${cleanState}`,
      );
      return;
    }
    const cleanCity = toCleanSlug(citySlug);
    if (updateType === 'Yearly' && selectedYear) {
      router.push(
        `/gold-silver-prices/${metalSlug}/${cleanState}/${cleanCity}/yearly/${selectedYear}`,
      );
      return;
    }
    if (updateType === 'Yearly') {
      router.push(`/gold-silver-prices/${metalSlug}/${cleanState}/${cleanCity}/yearly`);
      return;
    }
    if (updateType === 'Monthly' && selectedMonth) {
      router.push(
        `/gold-silver-prices/${metalSlug}/${cleanState}/${cleanCity}/monthly/${toMonthlySlug(selectedMonth)}`,
      );
      return;
    }
    if (updateType === 'Monthly') {
      router.push(`/gold-silver-prices/${metalSlug}/${cleanState}/${cleanCity}/monthly`);
      return;
    }
    router.push(
      `/gold-silver-prices/${metalSlug}/${cleanState}/${cleanCity}/daily/${initialDate}`,
    );
  };

  return (
    <section className='space-y-4 rounded-xl border border-gray-200 bg-white p-4'>
      <div className='grid gap-3 md:grid-cols-3'>
        <select
          className='rounded-md border border-gray-300 px-3 py-2 text-sm'
          value={stateSlug}
          onChange={(event) => {
            setStateSlug(event.target.value);
            setCitySlug('');
          }}
        >
          <option value=''>Select state</option>
          {stateOptions.map((state) => (
            <option key={state.slug} value={state.slug}>
              {state.label}
            </option>
          ))}
        </select>

        <select
          className='rounded-md border border-gray-300 px-3 py-2 text-sm disabled:cursor-not-allowed disabled:bg-gray-100'
          value={citySlug}
          disabled={!stateSlug}
          onChange={(event) => setCitySlug(event.target.value)}
        >
          <option value=''>Select city</option>
          {cityOptions.map((city) => (
            <option key={city.slug} value={city.slug}>
              {city.label}
            </option>
          ))}
        </select>

        <select
          className='rounded-md border border-gray-300 px-3 py-2 text-sm'
          value={updateType}
          onChange={(event) => {
            const nextType = event.target.value as UpdateType;
            setUpdateType(nextType);
            if (nextType !== 'Monthly') {
              setSelectedMonth('');
            }
            if (nextType !== 'Yearly') {
              setSelectedYear('');
            }
          }}
        >
          <option value='Daily'>Daily</option>
          <option value='Monthly'>Monthly</option>
          <option value='Yearly'>Yearly</option>
        </select>
        {updateType === 'Monthly' ? (
          <select
            className='rounded-md border border-gray-300 px-3 py-2 text-sm'
            value={selectedMonth}
            onChange={(event) => setSelectedMonth(event.target.value)}
          >
            <option value=''>Select month</option>
            {MONTH_OPTIONS.map((month) => (
              <option key={month} value={month}>
                {month}
              </option>
            ))}
          </select>
        ) : null}
        {updateType === 'Yearly' ? (
          <select
            className='rounded-md border border-gray-300 px-3 py-2 text-sm'
            value={selectedYear}
            onChange={(event) => setSelectedYear(event.target.value)}
          >
            <option value=''>Select year</option>
            {yearOptions.map((year) => (
              <option key={year} value={year}>
                {year}
              </option>
            ))}
          </select>
        ) : null}
      </div>

      <div className='flex items-center gap-3'>
        <button
          type='button'
          className='rounded-md bg-primary px-4 py-2 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:bg-gray-400'
          disabled={!stateSlug}
          onClick={onApply}
        >
          Apply
        </button>
        {/* <span className='text-xs text-gray-500'>
          {isLoading ? 'Fetching updates...' : 'Filters ready'}
        </span> */}
      </div>
    </section>
  );
}
